<template>
  <acv-dialog
    ref="termsOfServiceModal"
    v-model="modalIsOpen"
    class="terms-of-service-modal"
    title="Updated ACV Terms of Service"
    size="large"
    slide
    :click-away="false"
    disable-body-scrolling
    @closed="closeModal"
  >
    <template #header>
      <h6>Updated ACV Terms of Service</h6>
    </template>
    <template #body>
      <acv-loading-indicator
        v-if="!tosContent.length"
        variant="primary"
        :dots="3"
      />
      <acv-scrollbar
        v-else
        class="tos-scroll-container"
        @scroll="handleScroll"
      >
        <acv-banner :content="acceptanceText" />
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="tos-content"
          v-html="tosContent"
        />
      </acv-scrollbar>
    </template>

    <template #footer>
      <acv-button
        v-if="tosContent.length"
        id="download-tos-button"
        icon-type="download"
        title="Download Terms of Service"
        variant="tertiary"
        @click="downloadTos"
      >
        <template #prepend>
          <acv-icon icon="download" />
        </template>
        Download Terms of Service
      </acv-button>
      <div
        v-if="tosContent.length"
        class="tos-controls-wrapper"
      >
        <acv-checkbox
          v-model="confirmedTosRead"
          label="I have read and agree to the Terms of Service"
          icon-type="check"
          :disabled="!readAllTosContent"
        />
        <acv-button
          id="tos-submit-button"
          size="small"
          :disabled="!confirmedTosRead"
          @click="closeModal"
        >
          {{ ctaBtnText }}
        </acv-button>
      </div>
    </template>
  </acv-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import {
  AcvBanner,
  AcvButton,
  AcvCheckbox,
  AcvDialog,
  AcvLoadingIndicator,
  AcvIcon,
  AcvScrollbar,
} from '@acv/shared-vuejs';
import { mapActions, mapGetters } from 'vuex';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import htmlToPdfmake from 'html-to-pdfmake';

export default defineComponent({
  name: 'AcvTermsOfServiceModal',
  components: {
    AcvBanner,
    AcvButton,
    AcvCheckbox,
    AcvIcon,
    AcvLoadingIndicator,
    AcvDialog,
    AcvScrollbar,
  },
  data() {
    return {
      acceptanceText: 'Please accept our updated Terms to continue using ACV Marketplace',
      confirmedTosRead: false,
      modalIsOpen: true,
      readAllTosContent: false,
      tosContent: '',
      originalTitle: document.title,
    };
  },
  computed: {
    ...mapGetters({
      newTosContentUrl: 'newTosContentUrl',
    }),
    ctaBtnText() {
      return 'Continue to ACV Marketplace';
    },
  },
  async created() {
    pdfMake.vfs = pdfFonts.vfs;
    await this.getTosFromS3();
  },
  methods: {
    ...mapActions({
      setTosErrors: 'setTosErrors',
      submitTosAcceptance: 'submitTosAcceptance',
    }),
    closeModal() {
      this.submitTosAcceptance();
      this.modalIsOpen = false;
    },
    async getTosFromS3() {
      try {
        const response = await fetch(this.newTosContentUrl);
        const result = await response.json();
        this.tosContent = result.content;
      } catch (err) {
        this.setTosErrors(err);
        this.modalIsOpen = false;
      }
    },
    handleScroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      // reaching approximately to the very end of scroll bar
      if (Math.ceil(scrollTop + clientHeight) >= Math.floor(scrollHeight - 25)) {
        this.readAllTosContent = true;
      }
    },
    downloadTos() {
      // We need to remove non-asci characters that may be present in the document, most notably
      // %e2%80%8d (ZERO WIDTH JOINER), which can't be rendered properly in the PDF and is commonly
      // added by word processors
      const sanitizedContent = this.tosContent.replace(/[^\x20-\x7E]/g, '');
      // Then we need to convert the raw html to a format that can be processed by pdfmake
      const convertedContent = htmlToPdfmake(sanitizedContent);
      const documentDefinition = { content: convertedContent };
      pdfMake.createPdf(documentDefinition).download('ACV Terms of Service.pdf');
    },
  },
});
</script>

<style lang="scss" scoped>
  .tos-scroll-container {
    height: 600px;
  }

  .tos-content {
    margin-top: 1rem;
  }

  .tos-controls-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    > button {
      width: min-content;
      white-space: nowrap;
    }

    ::v-deep {
      .acv-input__label {
        line-height: 100% !important;
      }
    }
  }

  :deep(.legal-content-wrapper) {
    word-wrap: break-word;
    width: 100%;
    max-width: 530px;
  }

  ::v-deep {
    .acv-dialog.centered .acv-dialog-outer-content {
      pointer-events: none;
    }

    .acv-dialog-content {
      @apply tw-text-text-secondary;

      border-radius: 1rem;

      &__footer {
        button {
          text-align: left;
          padding-left: 0;

          @screen tw-md {
            padding-left: 0.75rem;
          }
        }
      }

      > .tw-flexbox-row-justify-end {
        justify-content: space-between;
        align-items: flex-start;
      }

      &.large {
        max-width: 100%;

        @screen tw-md {
          max-width: 800px;
        }
      }

      .acv-checkbox-wrapper {
        margin: 1rem 0;
      }

      .acv-checkbox {
        .acv-input__label {
          font-size: small;
        }
      }
    }

    .acv-banner__information {
      @apply tw-text-caution-dark;
    }

    .page-number {
      display: none;
    }

    .acv-banner {
      padding: 1rem 1rem 0.15rem;
      border-top: 0;

      @screen tw-md {
        padding: 1rem;
      }
    }

    h1.title {
      display: none;
    }

    a {
      @apply tw-text-text-link-on-light;
    }
  }
</style>
